import { FormattingSettings } from '@trimble/types';
import {
  Show,
  JSX,
  children,
  type Component,
  For,
  createSignal,
  createRenderEffect,
  on,
} from 'solid-js';
import { t } from '@trimble/i18n';
import {
  DEFAULT_VOLUME_TILESET_OPACITY,
  VolumeMeasurementResults,
} from '@trimble/web3d-providers';
import { Icon, TempSvgIcon } from '@tcweb/connect-ui';
import { FormattedArea, FormattedVolume } from '@trimble/ui-components/solidjs';
// eslint-disable-next-line custom/no-modus-v1 -- TWENG-132
import { Tab } from '@trimble-oss/modus-web-components/loader';
import {
  VolumeHeatmapStyle,
  VolumeHeatmapStyleProps,
} from '../volume-heatmap/VolumeHeatmapStyle';
import { MeasurementStyle } from '@trimble/measurements-shared';
import { RgbColor } from 'solid-color';
import type { LineStyle } from '@trimble/web3d-providers';

interface PropertyGroupProps {
  header: JSX.Element;
  children: JSX.Element | JSX.Element[];
}

const PropertyGroup: Component<PropertyGroupProps> = (props) => {
  const resolved = children(() => props.children);
  return (
    <section class="text-base-content flex flex-col gap-1">
      {props.header}
      <For each={resolved.toArray()}>
        {(child, index) => (
          <>
            <div
              class={`${index() > 0 ? 'mt-[1px]' : ''} bg-base-100 p-lg last-of-type:rounded-b-tab first-of-type:rounded-t-tab`}
            >
              {child}
            </div>
          </>
        )}
      </For>
    </section>
  );
};

const Header: Component<{
  title: string;
  icon: string | TempSvgIcon;
}> = (props) => {
  return (
    <section class="flex items-center gap-2">
      <Icon icon={props.icon} size="icon-small" />
      <modus-wc-typography hierarchy="p">{props.title}</modus-wc-typography>
    </section>
  );
};

export interface VolumetricMeasurementDetailsProps {
  measurementResult?: VolumeMeasurementResults;
  formattingSettings: FormattingSettings;
  /** Props forwarded to the VolumeHeatmapStyle component in the Style tab. */
  heatmapStyleProps?: Omit<VolumeHeatmapStyleProps, 'lengthUnit'>;
  /** When true, show the heatmap volume style editor in the Style tab. */
  showHeatmapStyle?: boolean;
  /** Current boundary color of the measurement. */
  boundaryColor?: RgbColor;
  /** Called when the user changes the boundary color. */
  onBoundaryColorChange?(color: RgbColor): void;
  /** Current volume heatmap tileset opacity (0–1). */
  tilesetOpacity?: number;
  /** Called when the user changes the volume heatmap tileset opacity. */
  onTilesetOpacityChange?(value: number): void;
  /** Current boundary line weight when a boundary is defined. */
  boundaryLineThickness?: number;
  /** Called when the user changes the boundary line weight. */
  onBoundaryLineThicknessChange?(value: number): void;
  /** Current boundary line style when a boundary is defined. */
  boundaryLineStyle?: LineStyle;
  /** Called when the user changes the boundary line style. */
  onBoundaryLineStyleChange?(value: LineStyle): void;
  /** Disables style editing controls. */
  disabled?: boolean;
  /** Tooltip shown when editing is disabled. */
  disabledReason?: string;
}

export const VolumetricMeasurementDetails = (
  props: VolumetricMeasurementDetailsProps
): JSX.Element | null => {
  const [selectedTab, setSelectedTab] = createSignal('properties');
  const [editedTilesetOpacity, setEditedTilesetOpacity] = createSignal<number>(
    DEFAULT_VOLUME_TILESET_OPACITY
  );

  createRenderEffect(
    on(
      () => props.tilesetOpacity,
      (tilesetOpacity) => {
        if (tilesetOpacity !== undefined) {
          setEditedTilesetOpacity(tilesetOpacity);
        }
      }
    )
  );

  const handleTilesetOpacityChange = (event: CustomEvent): void => {
    const value = Number.parseFloat(
      (event.detail?.target as HTMLInputElement)?.value ?? ''
    );
    if (Number.isNaN(value)) {
      return;
    }
    setEditedTilesetOpacity(value);
    props.onTilesetOpacityChange?.(value);
  };

  const tabs = (): Tab[] => {
    const ts: Tab[] = [
      {
        id: 'properties',
        label: t('siteVisualization.properties'),
        active: selectedTab() === 'properties',
      },
      {
        id: 'style',
        label: t('measurements.style'),
        active: selectedTab() === 'style',
      },
    ];
    return ts;
  };

  return (
    <Show when={!!props.measurementResult}>
      <div class="flex flex-col w-full">
        <div class="w-full shrink-0" data-testid="tabs-container">
          {/* eslint-disable-next-line custom/no-modus-v1 -- TWENG-132 */}
          <modus-tabs
            size="small"
            full-width={true}
            tabs={tabs()}
            on:tabChange={(e) => setSelectedTab(e.detail)}
            data-testid="volume-measurement-tabs"
          />
        </div>
        <div class="w-full">
          <Show when={selectedTab() === 'properties'}>
            <section class="flex text-base-content flex-col gap-2 w-full p-lg">
              <PropertyGroup
                header={
                  <Header
                    title={t('measurements.volumetric.coverageArea')}
                    icon="tc-icon-measure-area"
                  />
                }
              >
                <section class="">
                  <div
                    class="flex flex-col items-center justify-center"
                    data-testid="area-values"
                  >
                    <Show
                      when={
                        typeof props.measurementResult?.totalCoverageArea ===
                        'number'
                      }
                      fallback={<span>{t('general.notApplicable')}</span>}
                    >
                      <FormattedArea
                        currentUnit="m2"
                        settings={props.formattingSettings}
                        value={props.measurementResult!.totalCoverageArea!}
                        data-testid="area-total-value"
                      />
                    </Show>
                  </div>
                </section>
              </PropertyGroup>
              <PropertyGroup
                header={
                  <Header
                    title={t('measurements.volumetric.volume')}
                    icon="svg-compute-volume"
                  />
                }
              >
                <section class="">
                  <div
                    class="flex flex-col items-center justify-center"
                    data-testid="volume-total-values"
                  >
                    <Show
                      when={
                        typeof props.measurementResult?.totalVolume === 'number'
                      }
                      fallback={<span>{t('general.notApplicable')}</span>}
                    >
                      <FormattedVolume
                        settings={props.formattingSettings}
                        value={props.measurementResult!.totalVolume!}
                        currentUnit="m3"
                        data-testid="volume-total-value"
                      />
                    </Show>
                  </div>
                </section>
              </PropertyGroup>
              <PropertyGroup
                header={
                  <Header
                    title={t('measurements.volumetric.cutAndFill')}
                    icon="svg-cut-fill"
                  />
                }
              >
                <section
                  class="grid grid-cols-2 gap-1"
                  data-testid="cut-and-fill-values"
                >
                  <div class="flex flex-col items-center">
                    <span>{t('measurements.volumetric.cut')}</span>
                    <Show
                      when={
                        typeof props.measurementResult?.totalCutVolume ===
                        'number'
                      }
                      fallback={<span>{t('general.notApplicable')}</span>}
                    >
                      <FormattedVolume
                        settings={props.formattingSettings}
                        value={props.measurementResult!.totalCutVolume!}
                        currentUnit="m3"
                        data-testid="volume-cut-value"
                      />
                    </Show>
                  </div>
                  <div class="flex flex-col items-center">
                    <span>{t('measurements.volumetric.fill')}</span>
                    <Show
                      when={
                        typeof props.measurementResult?.totalFillVolume ===
                        'number'
                      }
                      fallback={<span>{t('general.notApplicable')}</span>}
                    >
                      <FormattedVolume
                        settings={props.formattingSettings}
                        value={props.measurementResult!.totalFillVolume!}
                        currentUnit="m3"
                        data-testid="volume-fill-value"
                      />
                    </Show>
                  </div>
                </section>
                <section
                  class="flex justify-center"
                  data-testid="balance-values"
                >
                  <div class="flex flex-col items-center">
                    <span>{t('measurements.volumetric.balance')}</span>
                    <Show
                      when={
                        typeof props.measurementResult?.totalBalanceVolume ===
                        'number'
                      }
                      fallback={<span>{t('general.notApplicable')}</span>}
                    >
                      <FormattedVolume
                        settings={props.formattingSettings}
                        value={props.measurementResult!.totalBalanceVolume!}
                        currentUnit="m3"
                        data-testid="volume-balance-value"
                      />
                    </Show>
                  </div>
                </section>
              </PropertyGroup>
            </section>
          </Show>
          <Show when={selectedTab() === 'style'}>
            <section class="flex flex-col gap-2 w-full p-lg">
              <Show when={props.showHeatmapStyle && props.heatmapStyleProps}>
                {(heatmapStyleProps) => (
                  <VolumeHeatmapStyle
                    {...heatmapStyleProps()}
                    lengthUnit={props.formattingSettings.lengthUnit}
                    canEdit={!props.disabled}
                  />
                )}
              </Show>
              <Show when={props.boundaryColor && props.onBoundaryColorChange}>
                <div class="border-t border-gray-0" />
                <div class="flex flex-col gap-4 pt-2">
                  <div class="flex flex-col gap-3">
                    <span class="text-sm font-medium text-gray-8 uppercase">
                      {t('measurements.volumetric.boundary')}
                    </span>
                    <MeasurementStyle
                      class="flex flex-col gap-4"
                      color={props.boundaryColor!}
                      onColorChange={props.onBoundaryColorChange!}
                      lineThickness={props.boundaryLineThickness}
                      lineStyle={props.boundaryLineStyle}
                      onLineThicknessChange={
                        props.onBoundaryLineThicknessChange
                      }
                      onLineStyleChange={props.onBoundaryLineStyleChange}
                      disabled={props.disabled}
                      disabledReason={props.disabledReason}
                    />
                  </div>
                </div>
              </Show>
              <Show
                when={
                  props.tilesetOpacity !== undefined &&
                  props.onTilesetOpacityChange
                }
              >
                <div class="border-t border-gray-0" />
                <div class="flex flex-col gap-4 pt-2">
                  <div class="text-sm text-gray-8">
                    {t('measurements.measurementStyle.transparency')}
                  </div>
                  <modus-wc-tooltip
                    content={props.disabledReason}
                    position="bottom"
                    disabled={!props.disabled}
                  >
                    <modus-wc-slider
                      size="sm"
                      data-testid="volume-tileset-opacity-slider"
                      class="h-5"
                      attr:min={0.01}
                      attr:max={1}
                      attr:step={0.01}
                      attr:value={editedTilesetOpacity()}
                      attr:disabled={props.disabled}
                      on:inputChange={handleTilesetOpacityChange}
                    />
                  </modus-wc-tooltip>
                </div>
              </Show>
            </section>
          </Show>
        </div>
      </div>
    </Show>
  );
};
